/**
 * FAQ Section — product-faq.js
 * Place in: assets/product-faq.js
 *
 * Features:
 *  - Smooth animated accordion (no CSS max-height hack)
 *  - Full keyboard accessibility (Enter / Space / Arrow keys)
 *  - Respects prefers-reduced-motion
 *  - Shopify Theme Editor live-reload support
 *  - "Open first item" option read from data attribute
 */

(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DURATION = REDUCED_MOTION ? 0 : 280; // ms

  class FAQSection {
    constructor(el) {
      this.section   = el;
      this.items     = Array.from(el.querySelectorAll('.faq-item'));
      this.triggers  = Array.from(el.querySelectorAll('.faq-item__trigger'));
      this.openFirst = el.dataset.openFirst === 'true';
      this.layout    = el.dataset.layout || 'single';

      // Apply layout class
      if (this.layout === 'two-col') {
        el.setAttribute('data-layout', 'two-col');
      }

      this._bindEvents();

      if (this.openFirst && this.triggers.length > 0) {
        this._open(this.triggers[0], false /* no animation on load */);
      }
    }

    _bindEvents() {
      this.triggers.forEach((trigger, i) => {
        trigger.addEventListener('click', () => this._toggle(trigger));

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = this.triggers[i + 1] || this.triggers[0];
            next.focus();
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = this.triggers[i - 1] || this.triggers[this.triggers.length - 1];
            prev.focus();
          }
          if (e.key === 'Home') { e.preventDefault(); this.triggers[0].focus(); }
          if (e.key === 'End')  { e.preventDefault(); this.triggers[this.triggers.length - 1].focus(); }
        });
      });
    }

    _toggle(trigger) {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      isOpen ? this._close(trigger) : this._open(trigger);
    }

    _open(trigger, animate = true) {
      const body    = document.getElementById(trigger.getAttribute('aria-controls'));
      const content = body.querySelector('.faq-item__answer');

      trigger.setAttribute('aria-expanded', 'true');
      body.removeAttribute('hidden');
      body.style.visibility = 'visible';

      const targetH = content.offsetHeight;

      if (!animate || DURATION === 0) {
        body.style.height = targetH + 'px';
        return;
      }

      // Animate open
      body.style.height = '0px';
      body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        body.style.transition = `height ${DURATION}ms cubic-bezier(0.4,0,0.2,1)`;
        body.style.height = targetH + 'px';

        body.addEventListener('transitionend', () => {
          body.style.height   = '';
          body.style.overflow = '';
          body.style.transition = '';
        }, { once: true });
      });
    }

    _close(trigger) {
      const body = document.getElementById(trigger.getAttribute('aria-controls'));

      trigger.setAttribute('aria-expanded', 'false');

      if (DURATION === 0) {
        body.style.height = '0px';
        body.style.visibility = 'hidden';
        body.setAttribute('hidden', '');
        return;
      }

      // Animate close
      const startH = body.offsetHeight;
      body.style.height    = startH + 'px';
      body.style.overflow  = 'hidden';
      body.style.transition = `height ${DURATION}ms cubic-bezier(0.4,0,0.2,1)`;

      requestAnimationFrame(() => {
        body.style.height = '0px';

        body.addEventListener('transitionend', () => {
          body.style.height     = '';
          body.style.overflow   = '';
          body.style.transition = '';
          body.style.visibility = 'hidden';
          body.setAttribute('hidden', '');
        }, { once: true });
      });
    }
  }

  // ── Init ──────────────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('.faq-section').forEach(el => {
      if (!el._faqInstance) {
        el._faqInstance = new FAQSection(el);
      }
    });
  }

  // Standard page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor events
  document.addEventListener('shopify:section:load',   (e) => {
    const section = e.target.querySelector('.faq-section');
    if (section) {
      section._faqInstance = null;
      section._faqInstance = new FAQSection(section);
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const trigger = e.target.querySelector('.faq-item__trigger');
    if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
      trigger.click();
    }
  });
})();
